import { useEffect, useState } from "react";

/**
 * Has this session accepted the terms?
 *
 * Extracted from `layouts/Default` (which owned it) and `pages/Home` (which had its own copy),
 * because a THIRD reader now needs the same answer: `SupportAssistantHost`, which is mounted
 * above the router's `<Suspense>` boundary and so cannot be handed the layout's state.
 *
 * Three readers of one sessionStorage key, each with its own `useState` initialiser and its own
 * event wiring, is the shape that drifts — the Home copy already lacked the cross-tab
 * `storage` listener the layout had.
 *
 * ⚠️ THE KEY IS `sessionStorage`, NOT `localStorage`, AND THAT IS THE PRODUCT RULE.
 * Acceptance lasts a browser session; a new session is prompted again. Don't "fix" the
 * re-prompt by moving it.
 *
 * ⚠️ TWO EVENTS, AND BOTH ARE LOAD-BEARING.
 * `storage` fires in OTHER tabs only — the tab that wrote the value never hears its own write.
 * So accepting the terms also dispatches a same-tab `consentAccepted` event, which is what
 * makes the layout re-render around the user immediately instead of on the next navigation.
 */

export const CONSENTS_ACCEPTED_KEY = "consentsAccepted";

/** Same-tab notification. `storage` covers the other tabs; this one covers the writer. */
export const CONSENT_ACCEPTED_EVENT = "consentAccepted";

/**
 * Read the flag, tolerating a `sessionStorage` that throws.
 *
 * Access can throw outright — Safari's private mode historically did, and a locked-down
 * enterprise policy can — and this runs during render as a `useState` initialiser, where a
 * throw is a white screen rather than a missing consent banner.
 */
export const readHasAcceptedTos = (): boolean => {
  try {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(CONSENTS_ACCEPTED_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Record the acceptance and tell this tab about it.
 *
 * The dispatch is not optional: without it the writing tab holds the pre-acceptance value until
 * something else re-renders it, which is how the terms screen used to linger after "Accept".
 */
export const acceptTos = (): void => {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(CONSENTS_ACCEPTED_KEY, "true");
  } catch {
    // Nothing useful to do: the user has accepted, the app should let them in for this render
    // even if the flag cannot be persisted. The event below still fires.
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CONSENT_ACCEPTED_EVENT));
  }
};

/** Subscribes to both events, so every reader stays in step in this tab and across tabs. */
export const useHasAcceptedTos = (): boolean => {
  const [hasAccepted, setHasAccepted] = useState<boolean>(readHasAcceptedTos);

  useEffect(() => {
    const sync = () => setHasAccepted(readHasAcceptedTos());

    // Re-read on mount as well as on the events: a component mounted after the acceptance
    // (the assistant host is mounted on every route change) would otherwise carry the value
    // its initialiser happened to see.
    sync();

    window.addEventListener(CONSENT_ACCEPTED_EVENT, sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener(CONSENT_ACCEPTED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return hasAccepted;
};

export default useHasAcceptedTos;
